/*============================================================================
  HC - IB cart drawer v2 save cart - 2026-08-20

  Fills the Save cart triggers and the two-step [data-ib-cart-modal="save"]
  dialog that snippets/ib-cart-drawer.liquid and
  snippets/ib-cart-drawer-modals.liquid render behind settings.cart_save_enabled.

  TRIGGERS ARE PLURAL, THE ACTION IS NOT. The drawer renders two
  [data-ib-cart-save] buttons - the labelled one in the tools row and the
  bookmark in the header, which stays reachable on a cart long enough to scroll
  the tools row away. Both are bound by the one controller below, so there is a
  single in-flight guard across them and no path where two doors mean two saves.

  ---------------------------------------------------------------------------
  BACKEND CONTRACT (IB Pro, reached over the Shopify app proxy - same origin,
  cookie session, so every request is credentials:'same-origin' and no token is
  ever held client-side). Base path comes off the drawer root's
  data-ib-proxy-root stamp, NOT a constant in this file.

    POST <root>/saved-carts
      {
        "targetCustomerGid": "gid://shopify/Customer/<id>",
        "name": "<name>",
        "payload": {
          "version": 1,
          "lines": [{
            "variantId": "gid://shopify/ProductVariant/<numeric variant_id>",
            "quantity": <int>,
            "titleSnapshot": "<product title>",
            "skuSnapshot": "<sku>"
          }],
          "orderNote": "<cart note>"
        }
      }
      -> 201 { "cart": { "id": "<uuid>", ... } }
      -> 401 { "error": "Not authenticated" }   (guest)

    POST <root>/saved-carts/<id>/share-links
      { "permission": "preview" }
      -> 201 { "shareUrl": "/apps/order/c/<token>" }

  shareUrl is a PATH. It is displayed and copied as location.origin + shareUrl
  so a preview link minted on the .ca storefront never hard-codes a host, and
  the token NEVER reaches storage - see the stash note below.

  Neither route is rate limited server-side, which makes the client the only
  thing standing between a double-click and two saved carts: one in-flight
  guard, button disabled while it runs.
  ---------------------------------------------------------------------------

  WHY theme.cart.getCart() AND NOT /cart.js: the cart layer dedupes concurrent
  readers onto a single request, drops that shared read the moment a mutation
  settles (so a post-write read always goes back to the network) and suppresses
  work while a Cloudflare challenge is active.

  What it does NOT do is hold a read behind a pending write — reads and writes
  are not serialised against each other. So a quantity the shopper just typed,
  still inside theme-cart.js's 300ms line-write debounce, or a note still inside
  CartForm's 500ms one, would simply not be in the snapshot. openSave() asks the
  drawer controller to flush both and waits for them to land before it reads.

  ENGINE-OWNED LINES ARE EXCLUDED. Gift lines (_ib_reward / _Gifted) are
  inserted by the gift engine from the manifest; saving them would
  double-insert on reload, because loading the cart re-runs the engine and it
  re-earns them. Intro-deal bundles (_ib_deal) are dropped for the mirror-image
  reason: the marker is what makes the engine OWN that line, and a snapshot
  carries no properties. Restored, the bundle would come back unowned — never
  removed when the cart drops below its threshold and Discount Kit stops
  pricing it, while the engine offers the deal again on top of it. Both cases
  are counted, and the modal says so out loud rather than silently dropping
  items.

  GUEST RESUME: a 401 stashes the SNAPSHOT (never the share token - cart
  contents are not a credential) in sessionStorage and bounces through
  /account/login. On the way back this module replays that snapshot, not the
  live cart, so a shopper who signed in on another tab still saves what they
  were looking at when they clicked.
==============================================================================*/
(function() {
  'use strict';

  var SELECTORS = {
    root: '[data-ib-cart-drawer]',
    pageRoot: '[data-ib-cart-page]',
    saveBtn: '[data-ib-cart-save]',
    modal: '[data-ib-cart-modal="save"]',
    card: '[role="dialog"]',
    step1: '[data-ib-save-step="1"]',
    step2: '[data-ib-save-step="2"]',
    name: '[data-ib-save-name]',
    meta: '[data-ib-save-meta]',
    giftNote: '[data-ib-save-gift-note]',
    error: '[data-ib-save-error]',
    loginWrap: '[data-ib-save-login-wrap]',
    login: '[data-ib-save-login]',
    submit: '[data-ib-save-submit]',
    savedName: '[data-ib-save-saved-name]',
    share: '[data-ib-save-share]',
    link: '[data-ib-save-link]',
    copy: '[data-ib-save-copy]',
    whatsapp: '[data-ib-save-whatsapp]',
    email: '[data-ib-save-email]',
    note: '[data-ib-save-note]',
    retryWrap: '[data-ib-save-retry-wrap]',
    retry: '[data-ib-save-retry]',
    done: '[data-ib-save-done]'
  };

  // The card is one dialog wearing two faces, so its accessible NAME has to be
  // one of two ids rather than a constant. Both are stamped in
  // snippets/ib-cart-drawer-modals.liquid on nodes that are always present in
  // their step; step 2's is the saved-as line, not the share heading, because
  // the share block is hidden wholesale when the link mint fails.
  var LABEL_STEP1 = 'IBCartSaveTitle';
  var LABEL_STEP2 = 'IBCartSaveSavedTitle';

  var STORAGE_KEY = 'ib:pending-save-cart';
  var PENDING_VERSION = 1;
  var PENDING_TTL_MS = 30 * 60 * 1000;
  var REQUEST_TIMEOUT_MS = 10000;
  var COPIED_MS = 1600;

  var VARIANT_GID_PREFIX = 'gid://shopify/ProductVariant/';

  // Classic customer accounts (templates/customers/login.json exists in this
  // theme), which honour return_url. If the shop is ever flipped to new
  // customer accounts the redirect lands on /account instead and return_url is
  // ignored - the resume still works, because it keys off sessionStorage and
  // not off where the shopper lands.
  var LOGIN_PATH = '/account/login';

  // code -> [bridge key, English fallback]. Read through message() so a locale
  // override lands without changing any branch that already speaks in codes.
  var MESSAGES = {
    rate_limited: ['cartSaveErrorRateLimited', 'Too many saves right now. Try again in a moment.'],
    too_many_lines: ['cartSaveErrorTooManyLines', 'This cart has too many lines to save.'],
    invalid_request: ['cartSaveErrorInvalidRequest', 'We couldn’t save this cart. Check the name and try again.'],
    unavailable: ['cartSaveErrorUnavailable', 'Saving carts is unavailable right now. Try again shortly.'],
    network: ['cartSaveErrorNetwork', 'We couldn’t reach the server. Check your connection and try again.'],
    unauthenticated: ['cartSaveErrorUnauthenticated', 'Sign in to save this cart, then try again.'],
    no_cart: ['cartSaveErrorNoCart', 'We couldn’t read your cart. Close this and try again.'],
    share_failed: ['cartSaveErrorShareFailed', 'Saved. We couldn’t create a preview link just now — open the cart from Saved carts to share it.'],
    fallback: ['cartSaveErrorFallback', 'Something went wrong saving this cart. Try again.']
  };

  function message(code) {
    var entry = Object.prototype.hasOwnProperty.call(MESSAGES, code) ? MESSAGES[code] : MESSAGES.fallback;
    return themeString(entry[0], entry[1]);
  }

  /*==========================================================================
    Utilities
  ==========================================================================*/

  function trim(value) {
    return String(value === null || value === undefined ? '' : value).replace(/^\s+|\s+$/g, '');
  }

  // NodeList.prototype.forEach is missing on the older Safari/Android builds
  // this theme still serves, and the rest of this file is ES5 for the same
  // reason. One conversion at boot beats a guard at every use site.
  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function money(cents) {
    var value = parseInt(cents, 10);
    if (isNaN(value)) {
      return '';
    }
    // Deliberately NOT theme.Currency.formatMoney: this store's money format
    // carries HTML ($65<sup>80</sup>), which is unsafe for textContent writes
    // and wrong for the plain-money style these surfaces use (per the mockup).
    var dollars = Math.floor(Math.abs(value) / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var centsPart = String(Math.abs(value) % 100);
    if (centsPart.length < 2) {
      centsPart = '0' + centsPart;
    }
    return (value < 0 ? '-' : '') + '$' + dollars + '.' + centsPart;
  }

  /*
    HC - locale strings (PR E) - 2026-08-20
    Same bridge as assets/ib-cart-drawer.js: layout/theme.liquid publishes the
    cart.general.save_* keys into theme.strings with [token] placeholders, and
    the English fallback stays here so a missing bridge degrades to English.
  */
  // Shopify's `t` filter HTML-escapes every non-_html translation, so bridged
  // strings arrive entity-encoded ("You&#39;ve ..."). Decode ONCE here; without
  // this, render-time escaping doubled the entities and they showed literally.
  // Shopify's escaper only ever emits these five entities, so a pure string
  // decode covers the bridge without touching the DOM.
  function decodeEntities(value) {
    if (typeof value !== 'string' || value.indexOf('&') === -1) {
      return value;
    }
    return value
      .replace(/&#0*39;|&apos;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  }

  function themeString(key, fallback, tokens) {
    var strings = typeof theme !== 'undefined' && theme.strings ? theme.strings : null;
    var value = strings && typeof strings[key] === 'string' && strings[key] ? decodeEntities(strings[key]) : fallback;
    if (!tokens) {
      return value;
    }
    for (var token in tokens) {
      if (Object.prototype.hasOwnProperty.call(tokens, token)) {
        value = value.split('[' + token + ']').join(tokens[token]);
      }
    }
    return value;
  }

  function itemsLabel(count) {
    return count === 1
      ? themeString('cartItemsCountOne', '[count] item', { count: count })
      : themeString('cartItemsCountMany', '[count] items', { count: count });
  }

  function unitsLabel(count) {
    return count === 1
      ? themeString('cartUnitsCountOne', '[count] unit', { count: count })
      : themeString('cartUnitsCountMany', '[count] units', { count: count });
  }

  function setHidden(el, hidden) {
    if (!el) {
      return;
    }
    if (hidden) {
      el.hidden = true;
      el.setAttribute('hidden', 'hidden');
    } else {
      el.hidden = false;
      el.removeAttribute('hidden');
    }
  }

  function textInto(el, value) {
    if (el) {
      el.textContent = value;
    }
  }

  // "Order Aug 20" - en-CA short month, matching the mockup's prefill.
  function defaultName(now) {
    var date = now || new Date();
    var stamp;
    try {
      stamp = date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
    } catch (error) {
      stamp = '';
    }
    return trim(themeString('cartSaveDefaultName', 'Order [date]', { date: stamp || '' }));
  }

  /*==========================================================================
    sessionStorage - throw-proof

    Private-mode Safari and storage-partitioned embeds throw on access, not on
    a feature check, so every call site goes through here and treats a throw as
    "no storage" rather than letting it take the click handler down.
  ==========================================================================*/

  var storage = {
    available: function() {
      if (typeof theme !== 'undefined' && theme && theme.config && theme.config.hasSessionStorage === false) {
        return false;
      }
      try {
        return Boolean(window.sessionStorage);
      } catch (error) {
        return false;
      }
    },

    read: function(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },

    write: function(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
        return true;
      } catch (error) {
        return false;
      }
    },

    remove: function(key) {
      try {
        window.sessionStorage.removeItem(key);
        return true;
      } catch (error) {
        return false;
      }
    }
  };

  /*==========================================================================
    Proxy client

    Ported from the account line's assets/ib-account.js: one envelope for every
    outcome so callers branch on `code` and never re-derive meaning from a
    status number. Copied deliberately rather than shared - the two files ship
    in different repos and must be free to drift.

    Rate limits: the Shopify proxy STRIPS X-RateLimit-* headers, so a 429 is
    read from Retry-After and the JSON body only.
  ==========================================================================*/

  function createClient(proxyRoot) {
    function envelope(code, status, data, extra) {
      var out = { ok: false, code: code, status: status, data: data || null };
      if (extra) {
        for (var key in extra) {
          if (Object.prototype.hasOwnProperty.call(extra, key)) {
            out[key] = extra[key];
          }
        }
      }
      return out;
    }

    function retryAfterSeconds(response, body) {
      var header = response.headers && typeof response.headers.get === 'function'
        ? response.headers.get('Retry-After')
        : null;
      var parsed = parseInt(header, 10);
      if (isFinite(parsed) && parsed > 0) {
        return parsed;
      }

      var fromBody = body && (body.retryAfter || body.retry_after);
      parsed = parseInt(fromBody, 10);
      if (isFinite(parsed) && parsed > 0) {
        return parsed;
      }

      return null;
    }

    function classify(response, body) {
      var status = response.status;
      var bodyError = (body && body.error) || '';

      if (response.ok) {
        return { ok: true, code: 'ok', status: status, data: body };
      }
      if (status === 401) {
        return envelope('unauthenticated', status, body);
      }
      if (status === 403) {
        return envelope(bodyError || 'forbidden', status, body);
      }
      if (status === 404) {
        return envelope('not_found', status, body);
      }
      if (status === 409) {
        return envelope(bodyError || 'conflict', status, body);
      }
      if (status === 413) {
        return envelope(bodyError || 'too_many_lines', status, body);
      }
      if (status === 422) {
        return envelope(bodyError || 'invalid_request', status, body);
      }
      if (status === 429) {
        return envelope('rate_limited', status, body, {
          retryAfter: retryAfterSeconds(response, body)
        });
      }
      if (status === 503) {
        return envelope('unavailable', status, body, {
          feature: (body && body.feature) || null
        });
      }
      return envelope('server_error', status, body);
    }

    function request(path, options) {
      var opts = options || {};
      var init = {
        method: opts.method || 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      };

      if (opts.body !== undefined) {
        init.headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(opts.body);
      }

      if (typeof window.fetch !== 'function') {
        return Promise.resolve(envelope('network', 0, null));
      }

      // A hung proxy is indistinguishable from a dead one as far as the
      // shopper is concerned, so an abort we raised ourselves reports as
      // `network` rather than growing a fourth "try again" message.
      var controller = null;
      var timer = null;
      if (typeof AbortController === 'function') {
        controller = new AbortController();
        init.signal = controller.signal;
        timer = setTimeout(function() {
          controller.abort();
        }, REQUEST_TIMEOUT_MS);
      }

      function done(result) {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        return result;
      }

      return window
        .fetch(proxyRoot + path, init)
        .then(function(response) {
          return Promise.resolve()
            .then(function() {
              return response.json();
            })
            .then(null, function() {
              return null;
            })
            .then(function(body) {
              return done(classify(response, body));
            });
        })
        .then(null, function() {
          return done(envelope('network', 0, null));
        });
    }

    return { request: request };
  }

  /*==========================================================================
    Snapshot

    Built at click time from the cart the shopper is looking at, then carried
    by value through login round trips and retries. Everything downstream reads
    this object; nothing re-reads the live cart.
  ==========================================================================*/

  // Engine-owned lines: gifts it re-earns, and (HC - 2026-09-01) intro-deal
  // bundles it re-offers. Neither survives a save with its ownership marker,
  // so neither is saved. See the header for what a restored unowned bundle
  // would do.
  function isRewardLine(item) {
    var props = item && item.properties;
    if (!props || typeof props !== 'object') {
      return false;
    }
    return Boolean(props._ib_reward) || Boolean(props._Gifted) || Boolean(props._ib_deal);
  }

  function buildSnapshot(cart) {
    var items = (cart && cart.items) || [];
    var lines = [];
    var units = 0;
    var totalCents = 0;
    var excluded = 0;

    for (var i = 0; i < items.length; i += 1) {
      var item = items[i];

      if (isRewardLine(item)) {
        excluded += 1;
        continue;
      }

      var variantId = item.variant_id === undefined || item.variant_id === null
        ? item.id
        : item.variant_id;
      if (variantId === undefined || variantId === null || variantId === '') {
        continue;
      }

      var quantity = parseInt(item.quantity, 10);
      if (!isFinite(quantity) || quantity < 1) {
        quantity = 1;
      }

      lines.push({
        variantId: VARIANT_GID_PREFIX + variantId,
        quantity: quantity,
        titleSnapshot: item.product_title || item.title || '',
        skuSnapshot: item.sku || ''
      });

      units += quantity;

      // Summed per kept line rather than taken from cart.total_price: the meta
      // line describes what is being SAVED, and the cart total still carries
      // whatever the excluded gift lines contributed.
      var linePrice = parseInt(item.final_line_price, 10);
      if (isFinite(linePrice)) {
        totalCents += linePrice;
      }
    }

    return {
      lines: lines,
      orderNote: (cart && cart.note) || '',
      lineCount: lines.length,
      unitCount: units,
      totalCents: totalCents,
      excluded: excluded
    };
  }

  // Replay path: the stash carries lines and a note, never the display counts,
  // so money is simply absent from the meta line rather than guessed at.
  function snapshotFromPending(pending) {
    var lines = (pending && pending.lines) || [];
    var units = 0;

    for (var i = 0; i < lines.length; i += 1) {
      var quantity = parseInt(lines[i].quantity, 10);
      units += isFinite(quantity) ? quantity : 0;
    }

    return {
      lines: lines,
      orderNote: (pending && pending.orderNote) || '',
      lineCount: lines.length,
      unitCount: units,
      totalCents: null,
      excluded: 0
    };
  }

  function formatMeta(snapshot) {
    if (!snapshot) {
      return '';
    }
    var parts = [
      itemsLabel(snapshot.lineCount),
      unitsLabel(snapshot.unitCount)
    ];
    if (snapshot.totalCents !== null && snapshot.totalCents !== undefined) {
      var amount = money(snapshot.totalCents);
      if (amount) {
        parts.push(amount);
      }
    }
    return parts.join(' · ');
  }

  function messageFor(result) {
    if (!result) {
      return message('fallback');
    }
    if (result.code === 'rate_limited' && result.retryAfter) {
      return themeString(
        'cartSaveErrorRateLimitedSeconds',
        'Too many saves right now. Try again in [seconds]s.',
        { seconds: result.retryAfter }
      );
    }
    return message(result.code);
  }

  /*==========================================================================
    Controller
  ==========================================================================*/

  /*
    saveBtns is EVERY trigger on the surface, not one: the drawer renders the
    labelled tools-row button and a bookmark in its header, both
    [data-ib-cart-save] and both the same action. They share this one controller
    - one in-flight guard, one dialog, one snapshot - and the only thing that
    varies per trigger is where focus goes when the dialog closes, which
    this.saveBtn tracks. It starts at the first trigger so a guest resume, which
    opens the dialog with no click behind it, still restores focus somewhere in
    the drawer rather than at the top of the document.
  */
  function IBCartSave(root, saveBtns) {
    this.root = root;
    this.saveBtns = saveBtns;
    this.saveBtn = saveBtns[0];
    this.modal = document.querySelector(SELECTORS.modal);

    this.proxyRoot = root.getAttribute('data-ib-proxy-root') || '/apps/order/api';
    this.customerGid = root.getAttribute('data-ib-customer-gid') || '';
    this.client = createClient(this.proxyRoot);

    this.snapshot = null;
    this.inFlight = false;
    this.shareUrl = '';
    this.copyTimer = null;
    // Held for the mint retry below: the cart is persisted, only the link
    // failed, so a retry needs the id and the name and nothing else.
    this.savedCartId = '';
    this.savedName = '';

    if (this.modal) {
      this.card = this.modal.querySelector(SELECTORS.card);
      this.step1 = this.modal.querySelector(SELECTORS.step1);
      this.step2 = this.modal.querySelector(SELECTORS.step2);
      this.nameInput = this.modal.querySelector(SELECTORS.name);
      this.metaEl = this.modal.querySelector(SELECTORS.meta);
      this.giftNoteEl = this.modal.querySelector(SELECTORS.giftNote);
      this.errorEl = this.modal.querySelector(SELECTORS.error);
      this.loginWrap = this.modal.querySelector(SELECTORS.loginWrap);
      this.loginLink = this.modal.querySelector(SELECTORS.login);
      this.submitBtn = this.modal.querySelector(SELECTORS.submit);
      this.savedNameEl = this.modal.querySelector(SELECTORS.savedName);
      this.shareEl = this.modal.querySelector(SELECTORS.share);
      this.linkEl = this.modal.querySelector(SELECTORS.link);
      this.copyBtn = this.modal.querySelector(SELECTORS.copy);
      this.whatsappEl = this.modal.querySelector(SELECTORS.whatsapp);
      this.emailEl = this.modal.querySelector(SELECTORS.email);
      this.noteEl = this.modal.querySelector(SELECTORS.note);
      this.retryWrap = this.modal.querySelector(SELECTORS.retryWrap);
      this.retryBtn = this.modal.querySelector(SELECTORS.retry);
      this.doneBtn = this.modal.querySelector(SELECTORS.done);
    }

    this.boot();
  }

  IBCartSave.prototype = {
    boot: function() {
      this.bind();
      this.resume();
    },

    bind: function() {
      var self = this;

      this.saveBtns.forEach(function(btn) {
        btn.addEventListener('click', function(evt) {
          if (evt && evt.preventDefault) {
            evt.preventDefault();
          }
          // Whichever door the shopper came through is the door they go back
          // out of; openModal() hands this to the controller's focus stack.
          self.saveBtn = btn;
          self.openSave();
        });
      });

      if (!this.modal) {
        return;
      }

      if (this.nameInput) {
        this.nameInput.addEventListener('input', function() {
          self.clearError();
          self.updateSubmitState();
        });
        this.nameInput.addEventListener('keydown', function(evt) {
          if (evt && evt.key === 'Enter') {
            if (evt.preventDefault) {
              evt.preventDefault();
            }
            self.submit();
          }
        });
      }

      if (this.submitBtn) {
        this.submitBtn.addEventListener('click', function() {
          self.submit();
        });
      }

      if (this.copyBtn) {
        this.copyBtn.addEventListener('click', function() {
          self.copyLink();
        });
      }

      if (this.retryBtn) {
        this.retryBtn.addEventListener('click', function() {
          self.retryShare();
        });
      }

      // Done closes through the controller so focus restore, the scrim and the
      // Escape ordering all stay in one place.
      if (this.doneBtn) {
        this.doneBtn.addEventListener('click', function() {
          self.closeModal();
        });
      }
    },

    /*========================================================================
      Environment reads

      The logged-in stamp is server-rendered by the same session that would
      answer the POST, so a "false" is authoritative enough to skip a request
      whose only possible answer is 401. A "true" is NOT treated as proof: the
      session can expire while the page sits open, so the 401 branch below
      stays live for the optimistic case. That is the whole decision - fast
      path on the certain answer, server truth on the uncertain one.
    ========================================================================*/

    isLoggedIn: function() {
      return this.root.getAttribute('data-ib-logged-in') === 'true';
    },

    drawer: function() {
      return window.IBCartDrawer || null;
    },

    /*========================================================================
      Step 1
    ========================================================================*/

    openSave: function() {
      var self = this;

      if (this.inFlight || !this.modal) {
        return;
      }

      // Push the surface's debounced edits to the server and wait for them to
      // land BEFORE reading. Without this a shopper who types a quantity and
      // clicks Save inside the same second saves the cart as it was, not as it
      // is — and a saved cart is a promise about exactly that.
      this.flushSurfaceEdits().then(function() {
        return self.readCart();
      }).then(function(cart) {
        self.snapshot = cart ? buildSnapshot(cart) : null;
        self.showStep1(self.snapshot ? defaultName() : '');
        if (!self.snapshot) {
          self.showError(message('no_cart'));
        }
        self.openModal();
        self.focusName();
      });
    },

    /*
      The drawer controller owns both flush paths — the note's dirty flag and
      the per-input userDirty stamps — and it is the instance bound to whichever
      v2 surface is live, drawer or /cart page. No controller (or an older build
      without the helper) means no flush, not no save.
    */
    flushSurfaceEdits: function() {
      var drawer = this.drawer();
      if (!drawer || typeof drawer.flushPendingEdits !== 'function') {
        return Promise.resolve(false);
      }
      try {
        return Promise.resolve(drawer.flushPendingEdits()).then(null, function() {
          return false;
        });
      } catch (error) {
        return Promise.resolve(false);
      }
    },

    readCart: function() {
      if (typeof theme === 'undefined' || !theme || !theme.cart || typeof theme.cart.getCart !== 'function') {
        return Promise.resolve(null);
      }
      return Promise.resolve()
        .then(function() {
          return theme.cart.getCart();
        })
        .then(null, function() {
          return null;
        });
    },

    /* The step decides what the dialog is called. Screen readers announce the
       name on open and on demand, so a step-2 dialog still calling itself
       "Save this cart" describes a form that is no longer on screen. */
    labelStep: function(id) {
      if (this.card) {
        this.card.setAttribute('aria-labelledby', id);
      }
    },

    showStep1: function(name) {
      setHidden(this.step1, false);
      setHidden(this.step2, true);
      this.labelStep(LABEL_STEP1);

      if (this.nameInput) {
        this.nameInput.value = name || '';
      }

      textInto(this.metaEl, formatMeta(this.snapshot));
      setHidden(this.giftNoteEl, !(this.snapshot && this.snapshot.excluded > 0));

      this.clearError();
      this.updateSubmitState();
    },

    updateSubmitState: function() {
      if (!this.submitBtn) {
        return;
      }
      var ready = Boolean(this.snapshot) && trim(this.nameInput ? this.nameInput.value : '') !== '' && !this.inFlight;
      this.submitBtn.disabled = !ready;
    },

    setInFlight: function(inFlight) {
      this.inFlight = inFlight;
      // Every trigger, not just the clicked one: the routes carry no server-side
      // rate limit, so leaving the other door open would be the double-save the
      // single-flight guard exists to prevent.
      this.saveBtns.forEach(function(btn) {
        btn.disabled = inFlight;
      });
      if (this.submitBtn) {
        this.submitBtn.classList[inFlight ? 'add' : 'remove']('is-loading');
      }
      this.updateSubmitState();
    },

    // `text`, not `message`: message() is the module-level locale lookup and a
    // parameter of that name would shadow it for anything added here later.
    showError: function(text, loginHref) {
      textInto(this.errorEl, text);
      setHidden(this.errorEl, false);

      if (loginHref && this.loginLink) {
        this.loginLink.setAttribute('href', loginHref);
        setHidden(this.loginWrap, false);
      } else {
        setHidden(this.loginWrap, true);
      }
    },

    clearError: function() {
      textInto(this.errorEl, '');
      setHidden(this.errorEl, true);
      setHidden(this.loginWrap, true);
    },

    /*========================================================================
      Save

      Single-flight: the routes carry no server-side rate limit, so a second
      click landing between the POST and its response would create a duplicate
      saved cart. The guard is the whole defence.
    ========================================================================*/

    submit: function() {
      if (this.inFlight || !this.snapshot) {
        return;
      }

      var name = trim(this.nameInput ? this.nameInput.value : '') || defaultName();
      this.clearError();
      this.setInFlight(true);
      this.run(name, this.snapshot, true);
    },

    // allowStash is false on the replay path: a second 401 there means the
    // login round trip did not actually establish a session, and re-stashing
    // would bounce the shopper to /account/login forever.
    run: function(name, snapshot, allowStash) {
      var self = this;

      if (allowStash && !this.isLoggedIn()) {
        this.setInFlight(false);
        this.handleUnauthenticated(name, snapshot);
        return Promise.resolve();
      }

      return this.create(name, snapshot).then(function(result) {
        self.setInFlight(false);

        if (result.ok) {
          self.showStep2(name, result.shareUrl, result.shareFailed);
          return;
        }

        if (result.code === 'unauthenticated') {
          if (allowStash) {
            self.handleUnauthenticated(name, snapshot);
          } else {
            self.showError(message('unauthenticated'), LOGIN_PATH);
          }
          return;
        }

        self.showError(messageFor(result));
      });
    },

    create: function(name, snapshot) {
      var self = this;

      return this.client
        .request('/saved-carts', {
          method: 'POST',
          body: {
            targetCustomerGid: this.customerGid,
            name: name,
            payload: {
              version: PENDING_VERSION,
              lines: snapshot.lines,
              orderNote: snapshot.orderNote
            }
          }
        })
        .then(function(created) {
          if (!created.ok) {
            return created;
          }

          var id = created.data && created.data.cart && created.data.cart.id;
          if (!id) {
            // A 2xx means the cart IS persisted, even if the body is missing
            // its id. Reporting an error here re-enables submit, and a retry
            // POSTs a second create — the backend has no idempotency key, so
            // that mints a duplicate saved cart. Treat it as a save that just
            // can't share: step 2 without the link row, and (savedCartId left
            // empty) without the mint-retry control, which needs the id.
            return { ok: true, shareUrl: '', shareFailed: true };
          }

          self.savedCartId = String(id);
          return self.mintShareLink(id);
        });
    },

    // The cart is already persisted by the time this runs, so a failure here
    // is NOT a save failure: step 2 still shows, minus the link.
    mintShareLink: function(id) {
      return this.client
        .request('/saved-carts/' + encodeURIComponent(id) + '/share-links', {
          method: 'POST',
          body: { permission: 'preview' }
        })
        .then(function(minted) {
          var path = minted.ok && minted.data ? minted.data.shareUrl : '';
          if (!path) {
            return { ok: true, shareUrl: '', shareFailed: true };
          }
          return { ok: true, shareUrl: window.location.origin + path, shareFailed: false };
        });
    },

    /*========================================================================
      Step 2
    ========================================================================*/

    showStep2: function(name, shareUrl, shareFailed) {
      this.shareUrl = shareUrl || '';
      this.savedName = name;

      setHidden(this.step1, true);
      setHidden(this.step2, false);
      this.labelStep(LABEL_STEP2);

      textInto(this.savedNameEl, themeString('cartSaveSavedAs', 'Saved as “[name]”', { name: name }));
      setHidden(this.shareEl, !this.shareUrl);

      if (this.shareUrl) {
        textInto(this.linkEl, this.shareUrl);
        this.setShareTargets(name, this.shareUrl);
      }

      if (shareFailed) {
        textInto(this.noteEl, message('share_failed'));
        setHidden(this.noteEl, false);
      } else {
        textInto(this.noteEl, '');
        setHidden(this.noteEl, true);
      }

      // Offered only when a retry could actually work: the mint failed AND we
      // still know which cart to mint for.
      setHidden(this.retryWrap, !(shareFailed && this.savedCartId));

      this.resetCopyLabel();
      this.openModal();

      var focusTarget = this.shareUrl ? this.copyBtn : this.doneBtn;
      if (focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus();
      }
    },

    /*
      Mint retry. The save itself is done and must never run twice, so this
      re-runs the share-link mint ALONE against the stored cart id. Single
      flight through the same inFlight guard the save uses, and the result goes
      back through showStep2 so success and a second failure paint through one
      path rather than two.
    */
    retryShare: function() {
      var self = this;

      if (this.inFlight || !this.savedCartId) {
        return Promise.resolve();
      }

      this.setInFlight(true);
      setHidden(this.retryWrap, true);

      return this.mintShareLink(this.savedCartId).then(function(result) {
        self.setInFlight(false);
        self.showStep2(self.savedName, result.shareUrl, result.shareFailed);
      });
    },

    // Plain share intents, no tracking parameters: the link is already the
    // shopper's to hand out and nothing here needs to know where it went.
    setShareTargets: function(name, url) {
      var body = themeString(
        'cartSaveShareMessage',
        'Here’s my International Beauty cart “[name]”: [url]',
        { name: name, url: url }
      );

      if (this.whatsappEl) {
        this.whatsappEl.setAttribute('href', 'https://wa.me/?text=' + encodeURIComponent(body));
      }
      if (this.emailEl) {
        this.emailEl.setAttribute(
          'href',
          'mailto:?subject=' +
            encodeURIComponent(themeString('cartSaveShareSubject', 'Cart: [name]', { name: name })) +
            '&body=' + encodeURIComponent(body)
        );
      }
    },

    copyLink: function() {
      var self = this;

      if (!this.shareUrl || !this.copyBtn) {
        return;
      }

      function succeed() {
        self.markCopied();
      }

      if (window.navigator && window.navigator.clipboard && typeof window.navigator.clipboard.writeText === 'function') {
        window.navigator.clipboard.writeText(this.shareUrl).then(succeed, function() {
          if (self.legacyCopy(self.shareUrl)) {
            succeed();
          }
        });
        return;
      }

      if (this.legacyCopy(this.shareUrl)) {
        succeed();
      }
    },

    // execCommand is deprecated but is still the only copy path on iOS Safari
    // inside a non-secure context and on older Android WebViews.
    legacyCopy: function(value) {
      if (!document.execCommand || !document.createElement) {
        return false;
      }
      try {
        var field = document.createElement('textarea');
        field.value = value;
        field.setAttribute('readonly', 'readonly');
        document.body.appendChild(field);
        if (typeof field.select === 'function') {
          field.select();
        }
        var copied = document.execCommand('copy');
        document.body.removeChild(field);
        return Boolean(copied);
      } catch (error) {
        return false;
      }
    },

    markCopied: function() {
      var self = this;

      textInto(this.copyBtn, themeString('cartSaveCopied', 'Copied'));
      if (this.copyTimer) {
        clearTimeout(this.copyTimer);
      }
      this.copyTimer = setTimeout(function() {
        self.resetCopyLabel();
      }, COPIED_MS);
    },

    resetCopyLabel: function() {
      if (this.copyTimer) {
        clearTimeout(this.copyTimer);
        this.copyTimer = null;
      }
      textInto(this.copyBtn, themeString('cartSaveCopy', 'Copy'));
    },

    /*========================================================================
      Guest resume
    ========================================================================*/

    handleUnauthenticated: function(name, snapshot) {
      var returnTo = window.location.pathname + window.location.search;

      if (!storage.available() || !this.stash(name, snapshot, returnTo)) {
        // No storage means no round trip can carry the cart, so the honest
        // answer is to say so and leave the modal open with the name intact.
        this.showError(message('unauthenticated'), LOGIN_PATH + '?return_url=' + encodeURIComponent(returnTo));
        return;
      }

      window.location.assign(LOGIN_PATH + '?return_url=' + encodeURIComponent(returnTo));
    },

    stash: function(name, snapshot, returnTo) {
      var payload;
      try {
        payload = JSON.stringify({
          v: PENDING_VERSION,
          name: name,
          orderNote: snapshot.orderNote,
          lines: snapshot.lines,
          returnTo: returnTo,
          ts: Date.now()
        });
      } catch (error) {
        return false;
      }
      return storage.write(STORAGE_KEY, payload);
    },

    readPending: function() {
      var raw = storage.read(STORAGE_KEY);
      if (!raw) {
        return null;
      }
      try {
        return JSON.parse(raw);
      } catch (error) {
        return null;
      }
    },

    resume: function() {
      if (!this.modal || !storage.available()) {
        return;
      }

      var pending = this.readPending();
      if (!pending) {
        // Nothing, or unparseable - either way clear the slot so a corrupt
        // value cannot sit there for the rest of the session.
        if (storage.read(STORAGE_KEY)) {
          storage.remove(STORAGE_KEY);
        }
        return;
      }

      var age = Date.now() - (parseInt(pending.ts, 10) || 0);
      if (pending.v !== PENDING_VERSION || !pending.lines || !pending.lines.length || age > PENDING_TTL_MS || age < 0) {
        storage.remove(STORAGE_KEY);
        return;
      }

      // Still a guest: leave the key alone so the NEXT page - the one after a
      // successful sign-in - can pick it up.
      if (!this.isLoggedIn()) {
        return;
      }

      // Single attempt. The key goes before the request, not after it, so a
      // request that hangs, 500s or navigates away cannot be replayed on every
      // subsequent page load; the payload lives in memory from here on.
      storage.remove(STORAGE_KEY);

      this.replay(pending);
    },

    replay: function(pending) {
      var self = this;
      var snapshot = snapshotFromPending(pending);
      var name = trim(pending.name) || defaultName();

      this.snapshot = snapshot;
      this.showStep1(name);
      this.setInFlight(true);

      var drawer = this.drawer();
      if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) {
        drawer.open();
      }

      return this.run(name, snapshot, false).then(function() {
        // A failed replay lands on step 1 with the name and the error already
        // painted by run(); showing the modal here is what makes it visible.
        if (self.modal && self.modal.hidden) {
          self.openModal();
        }
      });
    },

    /*========================================================================
      Modal plumbing - delegated to the drawer controller so this file
      registers no key handler, no scrim listener and no focus stack of its
      own. It owns the step contents; the controller owns the dialog.
    ========================================================================*/

    openModal: function() {
      var drawer = this.drawer();
      if (drawer && typeof drawer.openModal === 'function') {
        drawer.openModal(this.modal, this.saveBtn);
        return;
      }
      setHidden(this.modal, false);
    },

    closeModal: function() {
      var drawer = this.drawer();
      if (drawer && typeof drawer.closeModal === 'function') {
        drawer.closeModal(this.modal);
      } else {
        setHidden(this.modal, true);
      }
      // Next open always starts at step 1, whichever step this one ended on —
      // and the name goes back with the contents, so the next open cannot
      // announce the share step of the cart saved before it.
      setHidden(this.step1, false);
      setHidden(this.step2, true);
      this.labelStep(LABEL_STEP1);
    },

    focusName: function() {
      if (this.nameInput && typeof this.nameInput.focus === 'function') {
        this.nameInput.focus();
        if (typeof this.nameInput.select === 'function') {
          this.nameInput.select();
        }
      }
    }
  };

  /*
    Two queries rather than one comma-joined selector, matching
    assets/ib-cart-drawer.js: the drawer and the v2 cart page are mutually
    exclusive by URL, so this is a stated precedence rather than a race. The
    page root carries the same data-ib-logged-in / data-ib-customer-gid /
    data-ib-proxy-root stamps this module reads, and replay()'s "open the drawer
    first" branch no-ops there on its own — the controller reports a page as
    permanently open.
  */
  function boot() {
    var root = document.querySelector(SELECTORS.root) || document.querySelector(SELECTORS.pageRoot);
    if (!root) {
      return;
    }

    // The buttons are the feature switch made visible: settings.cart_save_enabled
    // gates both the markup and this script, and the markup is the one that
    // survives a cached asset. querySelectorAll because the drawer renders two
    // triggers for the one action (tools row + header bookmark) behind that
    // single gate — no match at all still means the feature is off.
    var saveBtns = toArray(root.querySelectorAll(SELECTORS.saveBtn));
    if (!saveBtns.length) {
      return;
    }

    try {
      window.IBCartSave = new IBCartSave(root, saveBtns);
    } catch (error) {
      if (window.console && window.console.warn) {
        window.console.warn('[IBCartSave] boot failed', error);
      }
    }
  }

  // Exposed for tests: the pure half, where every mapping decision lives.
  if (typeof window !== 'undefined') {
    window.IBCartSaveModel = {
      buildSnapshot: buildSnapshot,
      createClient: createClient,
      defaultName: defaultName,
      formatMeta: formatMeta,
      isRewardLine: isRewardLine,
      messageFor: messageFor,
      snapshotFromPending: snapshotFromPending,
      storage: storage,
      STORAGE_KEY: STORAGE_KEY,
      PENDING_TTL_MS: PENDING_TTL_MS
    };
  }

  boot();
})();
